/**
 * *How far does a change go* — Board 14, `docs/visit-page.md` §18, D20; Slice
 * 1's shared helper (lead round 3): any edit of the plan, the crew, the crew
 * note or the time of a series visit asks it before it saves.
 *
 * Board 14: a 330 popover — the section label (*CHANGE THE CREW NOTE FOR*),
 * two radio rows (**This visit only** · *26 · Thu Oct 22*, selected on the
 * green band; **This one and the 4 after** · *26 – 30 · through Nov 19*), the
 * line *Visits already closed never change.*, then Cancel · **Save**. Drawn
 * beside what it is about (the board puts it to the right of the card, its top
 * 27 px under the card's); on a phone every choice is a sheet (law 6), so it
 * is Board 16 D's sheet there.
 *
 * The numbers are the server's (`SeriesPosition.after`); a series with nothing
 * after this visit offers *This visit only* alone.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ApplyTo, IsoDay, SeriesPosition } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useDismiss } from '../components/overlays.js'
import { COMMON, HOW_FAR } from './copy.js'
import { dayText } from './format.js'
import { VISIT_LAYER, VISIT_LAYER_OVER, VisitButton, VisitPopup } from './sheet.js'

export function HowFarPopover({
  anchor, series, date, heading = HOW_FAR.crewNote, onSave, onCancel, busy = false, over = false,
}: {
  /** What it is drawn beside — the card whose edit asked (Board 14: For the crew). */
  anchor: HTMLElement | null
  series: SeriesPosition
  /** This visit's day — *Thu Oct 22*. */
  date: IsoDay
  /** The section label; Board 14 draws the crew note's. */
  heading?: string
  onSave: (applyTo: ApplyTo) => void
  onCancel: () => void
  busy?: boolean
  /** Asked from a sheet (the edit sheet's Save) — drawn over it (`VISIT_LAYER_OVER`). */
  over?: boolean
}) {
  const layer = over ? VISIT_LAYER_OVER : VISIT_LAYER
  const phone = usePhone()
  const [choice, setChoice] = useState<ApplyTo>('this')
  const options = <Options series={series} date={date} choice={choice} onChoose={setChoice} />

  if (phone) {
    return (
      <VisitPopup
        width={330}
        title={heading}
        layer={layer}
        onClose={onCancel}
        footer={(
          <>
            <VisitButton size="phone" onClick={onCancel}>{HOW_FAR.cancel}</VisitButton>
            <VisitButton size="phone" tone="primary" disabled={busy} onClick={() => onSave(choice)}>{HOW_FAR.save}</VisitButton>
          </>
        )}
      >
        {options}
        <p className="m-0 mt-2 text-[11.5px] text-text-muted">{HOW_FAR.closedNeverChange}</p>
      </VisitPopup>
    )
  }

  return (
    <Floating anchor={anchor} onClose={onCancel} layer={layer} label={heading}>
      <div className="px-3.5 pb-1.5 pt-[9px] text-label uppercase leading-[normal] text-text-faint">{heading}</div>
      {options}
      <div className="px-4 pb-0.5 pt-1 text-[11.5px] text-text-muted">{HOW_FAR.closedNeverChange}</div>
      <div className="flex justify-end gap-2 px-3 pt-1.5">
        <button type="button" onClick={onCancel}
          className="inline-flex h-[30px] items-center rounded-button border border-line bg-surface px-3.5 text-[12px] font-semibold text-text hover:bg-hover">
          {HOW_FAR.cancel}
        </button>
        <button type="button" disabled={busy} onClick={() => onSave(choice)}
          className="inline-flex h-[30px] items-center rounded-button border border-accent bg-accent px-3.5 text-[12px] font-semibold text-white hover:bg-accent-dark disabled:opacity-60">
          {HOW_FAR.save}
        </button>
      </div>
    </Floating>
  )
}

function Options({ series, date, choice, onChoose }: {
  series: SeriesPosition; date: IsoDay; choice: ApplyTo; onChoose: (next: ApplyTo) => void
}) {
  const rows: { key: ApplyTo; title: string; line: string }[] = [
    { key: 'this', title: HOW_FAR.thisOnly, line: COMMON.dot(String(series.index), dayText(date, 'date')) },
  ]
  if (series.after.count > 0) {
    rows.push({
      key: 'this_and_after',
      title: HOW_FAR.thisAndAfter(String(series.after.count)),
      line: series.after.through
        ? HOW_FAR.range(String(series.index), String(series.after.lastIndex), dayText(series.after.through, 'monthDay'))
        : COMMON.range(String(series.index), String(series.after.lastIndex)),
    })
  }
  return (
    <div role="radiogroup" aria-label={HOW_FAR.crewNote}>
      {rows.map((row) => {
        const on = row.key === choice
        return (
          <button
            key={row.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChoose(row.key)}
            className={cn('mx-1 flex w-[calc(100%-8px)] items-start gap-2.5 rounded-[9px] px-3 py-[9px] text-left',
              on ? 'bg-green-band-bg' : 'hover:bg-hover')}
          >
            <span
              aria-hidden
              className={cn('mt-px box-border inline-block h-4 w-4 shrink-0 rounded-full bg-surface',
                on ? 'border-[5px] border-accent' : 'border-[1.5px] border-line-strong')}
            />
            <span className="flex flex-col">
              <b className="text-[12.5px]">{row.title}</b>
              <span className="text-[11.5px] text-text-muted">{row.line}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** The 330 box, fixed beside its anchor (right of it, 27 px down — Board 14), flipped under it where the window has no room. */
function Floating({ anchor, onClose, layer, label, children }: {
  anchor: HTMLElement | null; onClose: () => void; layer: number; label: string; children: React.ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  useDismiss(true, onClose, box, layer)

  useLayoutEffect(() => {
    const place = () => {
      const rect = anchor?.getBoundingClientRect()
      if (!rect) { setAt({ left: window.innerWidth / 2 - 165, top: window.innerHeight / 3 }); return }
      const right = rect.right + 16
      const height = box.current?.offsetHeight ?? 0
      if (right + 330 <= window.innerWidth - 8) { setAt({ left: right, top: rect.top + 27 }); return }
      const left = Math.max(8, rect.right - 330)
      /* No room under it (a sheet's footer): above it. */
      setAt(rect.bottom + 6 + height <= window.innerHeight - 8
        ? { left, top: rect.bottom + 6 }
        : { left, top: Math.max(8, rect.top - 6 - height) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [anchor])

  return createPortal((
    <div
      ref={box}
      role="dialog"
      aria-label={label}
      data-overlay-layer={layer}
      data-how-far=""
      className="fixed box-border w-[330px] rounded-[12px] border border-line bg-surface pb-2 pt-1 text-text shadow-[0_14px_40px_rgba(20,20,10,0.14)]"
      style={{ zIndex: layer, left: at?.left ?? -9999, top: at?.top ?? -9999 }}
    >
      {children}
    </div>
  ), document.body)
}
